/**
 * Caper Session Performance Schema Configuration
 *
 * Defines metrics, dimensions, formatters, and labels for the
 * Caper Session Performance dashboard.
 */

import { createSchemaConfig } from '../helpers/schema.helpers'
import {
  InsightsFormatterType,
  InsightsGroupByTypes,
  InsightsMetricDesiredDirection,
} from '../types/schema.types'
import { Metrics, Dimensions } from './caperSessionPerformance.types'

export { Metrics, Dimensions }

export const schemaConfig = createSchemaConfig({
  metrics: Metrics,
  dimensions: Dimensions,
  defaultDateDimension: Dimensions.DATE_PT,

  metricsConfig: {
    // Primary Overview Card Metrics
    [Metrics.COUNT_ORDERS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_ORDERS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.SUM_GMV]: {
      formatter: InsightsFormatterType.Currency,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.SUM_GMV',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_BASKET_VALUE]: {
      formatter: InsightsFormatterType.Currency,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_BASKET_VALUE',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_BASKET_SIZE]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_BASKET_SIZE',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.LOYALTY_USAGE_RATE]: {
      formatter: InsightsFormatterType.Percentage,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.LOYALTY_USAGE_RATE',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },

    // Additional metrics for future detailed analysis
    [Metrics.COUNT_SESSIONS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_SESSIONS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_COMPLETED_SESSIONS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_COMPLETED_SESSIONS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_ABANDONED_SESSIONS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_ABANDONED_SESSIONS',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.ABANDONMENT_RATE]: {
      formatter: InsightsFormatterType.Percentage,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.ABANDONMENT_RATE',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_NON_COMPLETED_SESSIONS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_NON_COMPLETED_SESSIONS',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },

    // User Order State Metrics
    [Metrics.COUNT_NEW_USERS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_NEW_USERS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_HABITUAL_USERS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_HABITUAL_USERS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_RESURRECTED_USERS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_RESURRECTED_USERS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_NEW_STORE_USERS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_NEW_STORE_USERS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_HABITUAL_STORE_USERS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_HABITUAL_STORE_USERS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_RESURRECTED_STORE_USERS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_RESURRECTED_STORE_USERS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },

    // NPS Metrics
    [Metrics.AVG_NPS_SCORE]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_NPS_SCORE',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_NPS_PROMOTERS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_NPS_PROMOTERS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_NPS_DETRACTORS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_NPS_DETRACTORS',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.NPS_RESPONSE_RATE]: {
      formatter: InsightsFormatterType.Percentage,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.NPS_RESPONSE_RATE',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_AUDITED_SESSIONS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_AUDITED_SESSIONS',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_DISTINCT_AUDIT_ISSUE_TYPES]: {
      formatter: InsightsFormatterType.Integer,
      label:
        'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_DISTINCT_AUDIT_ISSUE_TYPES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_DISTINCT_AUDIT_ISSUES]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_DISTINCT_AUDIT_ISSUES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.AUDIT_RATE]: {
      formatter: InsightsFormatterType.Percentage,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AUDIT_RATE',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.PERCENT_SESSIONS_WITHOUT_EMPLOYEE_INTERVENTION]: {
      formatter: InsightsFormatterType.Percentage,
      label:
        'insightsPortalDomain.caperReportingDashboard.metrics.PERCENT_SESSIONS_WITHOUT_EMPLOYEE_INTERVENTION',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_AUDIT_WAIT_TIME_MINUTES]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_AUDIT_WAIT_TIME_MINUTES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_FORCE_RESOLVED_AUDITS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_FORCE_RESOLVED_AUDITS',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.FORCE_RESOLVE_RATE]: {
      formatter: InsightsFormatterType.Percentage,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.FORCE_RESOLVE_RATE',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_RESOLVE_ALL_CLICKED]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_RESOLVE_ALL_CLICKED',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.RESOLVE_ALL_CLICKED_RATE]: {
      formatter: InsightsFormatterType.Percentage,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.RESOLVE_ALL_CLICKED_RATE',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.AVG_FLAGGED_ISSUES]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_FLAGGED_ISSUES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_SESSIONS_WITH_LOYALTY]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_SESSIONS_WITH_LOYALTY',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_SESSIONS_WITH_COUPONS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_SESSIONS_WITH_COUPONS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.SUM_COUPON_VALUE]: {
      formatter: InsightsFormatterType.Currency,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.SUM_COUPON_VALUE',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_COUPONS_PER_SESSION]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_COUPONS_PER_SESSION',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUPON_REDEMPTION_RATE]: {
      formatter: InsightsFormatterType.Percentage,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUPON_REDEMPTION_RATE',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_SESSION_DURATION_MINUTES]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_SESSION_DURATION_MINUTES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.AVG_ITEMS_IN_CART]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_ITEMS_IN_CART',
      tooltip: 'insightsPortalDomain.caperReportingDashboard.metrics.tooltip.AVG_ITEMS_IN_CART',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },

    // New Audit Metrics
    [Metrics.NO_AUDIT_RATE]: {
      formatter: InsightsFormatterType.Percentage,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.NO_AUDIT_RATE',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_AUDIT_RESOLVED_TIME_MINUTES]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_AUDIT_RESOLVED_TIME_MINUTES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.AVG_FLAGGED_ISSUES_RATE]: {
      formatter: InsightsFormatterType.Percentage,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_FLAGGED_ISSUES_RATE',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.AVG_CHECKOUT_AUDIT_SCORE]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_CHECKOUT_AUDIT_SCORE',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_FRAUD_THRESHOLD_EXCEEDED]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_FRAUD_THRESHOLD_EXCEEDED',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.FRAUD_THRESHOLD_EXCEEDED_RATE]: {
      formatter: InsightsFormatterType.Percentage,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.FRAUD_THRESHOLD_EXCEEDED_RATE',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },

    // Audit Issue Breakdown Metrics
    [Metrics.COUNT_AGE_RESTRICTED_ISSUES]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_AGE_RESTRICTED_ISSUES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_PERSONAL_ISSUES]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_PERSONAL_ISSUES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_WEIGHT_MISMATCH_ISSUES]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_WEIGHT_MISMATCH_ISSUES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_REMOVAL_MISMATCH_ISSUES]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_REMOVAL_MISMATCH_ISSUES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_PRODUCE_AUDIT_ISSUES]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_PRODUCE_AUDIT_ISSUES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_SCALE_RESET_ISSUES]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_SCALE_RESET_ISSUES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_SCALE_RESET_ADD_REMOVE_ISSUES]: {
      formatter: InsightsFormatterType.Integer,
      label:
        'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_SCALE_RESET_ADD_REMOVE_ISSUES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.SUM_AUDIT_ISSUES]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.SUM_AUDIT_ISSUES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.AVG_AUDIT_ISSUES_PER_SESSION]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_AUDIT_ISSUES_PER_SESSION',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_AUDIT_ISSUES_BY_TYPE]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_AUDIT_ISSUES_BY_TYPE',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },

    // QR Code Metrics
    [Metrics.COUNT_SESSIONS_SINGLE_QR]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_SESSIONS_SINGLE_QR',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_SESSIONS_MULTIPLE_QR]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_SESSIONS_MULTIPLE_QR',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.MULTIPLE_QR_RATE]: {
      formatter: InsightsFormatterType.Percentage,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.MULTIPLE_QR_RATE',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },

    // New Coupon Metrics
    [Metrics.SUM_COUPON_VALUE_CAPER_FUNDED]: {
      formatter: InsightsFormatterType.Currency,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.SUM_COUPON_VALUE_CAPER_FUNDED',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.SUM_COUPON_VALUE_DEALSHUB]: {
      formatter: InsightsFormatterType.Currency,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.SUM_COUPON_VALUE_DEALSHUB',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.SUM_COUPON_VALUE_ALERT]: {
      formatter: InsightsFormatterType.Currency,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.SUM_COUPON_VALUE_ALERT',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.SUM_COUPON_VALUE_OFFLINE]: {
      formatter: InsightsFormatterType.Currency,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.SUM_COUPON_VALUE_OFFLINE',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_COUPONS_PER_ORDER]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_COUPONS_PER_ORDER',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },

    // Duration Metrics
    [Metrics.AVG_CHECKOUT_DURATION_MINUTES]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_CHECKOUT_DURATION_MINUTES',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },

    // Item Metrics
    [Metrics.AVG_MAX_ITEMS_IN_CART]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_MAX_ITEMS_IN_CART',
      tooltip: 'insightsPortalDomain.caperReportingDashboard.metrics.tooltip.AVG_MAX_ITEMS_IN_CART',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.SUM_ITEMS_AT_CHECKOUT]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.SUM_ITEMS_AT_CHECKOUT',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_ITEMS_PER_SESSION]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_ITEMS_PER_SESSION',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_TRASH_CAN_CLICKS_PER_SESSION]: {
      formatter: InsightsFormatterType.Decimal,
      label:
        'insightsPortalDomain.caperReportingDashboard.metrics.AVG_TRASH_CAN_CLICKS_PER_SESSION',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },

    // Cart Utilization Metrics
    [Metrics.COUNT_UNIQUE_CARTS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_UNIQUE_CARTS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_SESSIONS_PER_CART]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_SESSIONS_PER_CART',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_SESSIONS_PER_CART]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_SESSIONS_PER_CART',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_USERS_PER_CART_PER_DAY]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_USERS_PER_CART_PER_DAY',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_AVAILABLE_CART_HOURS]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_AVAILABLE_CART_HOURS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.PEAK_DAILY_UTILIZATION]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.PEAK_DAILY_UTILIZATION',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.AVG_DAILY_INFERRED_STORE_HOURS]: {
      formatter: InsightsFormatterType.Decimal,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.AVG_DAILY_INFERRED_STORE_HOURS',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
    [Metrics.COUNT_AUDITED_CARTS]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_AUDITED_CARTS',
      desiredDirection: InsightsMetricDesiredDirection.NEGATIVE,
    },
    [Metrics.COUNT_ACTIVE_STORES]: {
      formatter: InsightsFormatterType.Integer,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.COUNT_ACTIVE_STORES',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },

    // ETL Metadata
    [Metrics.MAX_UPDATED_AT_DATE_TIME_UTC]: {
      formatter: InsightsFormatterType.Date,
      label: 'insightsPortalDomain.caperReportingDashboard.metrics.MAX_UPDATED_AT_DATE_TIME_UTC',
      desiredDirection: InsightsMetricDesiredDirection.POSITIVE,
    },
  },

  dimensionsConfig: {
    // Time Dimensions
    [Dimensions.DATE_PT]: {
      type: InsightsGroupByTypes.TimeInterval,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.DATE_PT',
      formatter: InsightsFormatterType.Date,
      date: true,
    },
    [Dimensions.WEEK_PT]: {
      type: InsightsGroupByTypes.TimeInterval,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.WEEK_PT',
      formatter: InsightsFormatterType.Date,
      date: true,
    },
    [Dimensions.MONTH_PT]: {
      type: InsightsGroupByTypes.TimeInterval,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.MONTH_PT',
      date: true,
    },
    [Dimensions.HOUR_PT]: {
      type: InsightsGroupByTypes.TimeInterval,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.HOUR_PT',
      formatter: InsightsFormatterType.Date,
      date: true,
    },

    // Core Access Control Dimensions
    [Dimensions.PARTNER]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.PARTNER',
    },
    [Dimensions.WAREHOUSE]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.WAREHOUSE',
    },
    [Dimensions.WAREHOUSE_LOCATION]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.WAREHOUSE_LOCATION',
    },
    [Dimensions.WAREHOUSE_LOCATION_STATE]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.WAREHOUSE_LOCATION_STATE',
    },
    [Dimensions.RETAILER]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.RETAILER',
    },

    // Store Dimensions
    [Dimensions.RETAILER_LOCATION_NAME]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.RETAILER_LOCATION_NAME',
    },
    [Dimensions.STORE_PARTNER_GROUP]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.STORE_PARTNER_GROUP',
    },
    [Dimensions.PARTNER_NAME]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.PARTNER_NAME',
    },
    [Dimensions.STORE_NICKNAME]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.STORE_NICKNAME',
    },
    [Dimensions.WAREHOUSE_ID]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.WAREHOUSE_ID',
    },
    [Dimensions.WAREHOUSE_LOCATION_ID]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.WAREHOUSE_LOCATION_ID',
    },
    [Dimensions.STORE_ID]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.STORE_ID',
    },
    [Dimensions.RETAILER_NAME]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.RETAILER_NAME',
    },

    // Session Attributes
    [Dimensions.NPS_LABEL]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.NPS_LABEL',
    },
    [Dimensions.HAS_NPS]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.HAS_NPS',
    },
    [Dimensions.ABANDONMENT_CATEGORY]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.ABANDONMENT_CATEGORY',
    },
    [Dimensions.AUDIT_RESOLUTION_METHOD]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.AUDIT_RESOLUTION_METHOD',
    },
    [Dimensions.HAS_LOYALTY_PROGRAM]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.HAS_LOYALTY_PROGRAM',
    },
    [Dimensions.IS_ABANDONED]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.IS_ABANDONED',
    },
    [Dimensions.IS_COMPLETED]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.IS_COMPLETED',
    },

    // User Order State Dimensions
    [Dimensions.USER_ORDER_STATE]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.USER_ORDER_STATE',
    },
    [Dimensions.STORE_USER_ORDER_STATE]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.STORE_USER_ORDER_STATE',
    },

    // Identifier Dimensions
    [Dimensions.SESSION_ID]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.SESSION_ID',
    },
    [Dimensions.SESSION_START_TIME_LOCAL]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.SESSION_START_TIME_LOCAL',
      formatter: InsightsFormatterType.Date,
      date: true,
    },
    [Dimensions.SESSION_END_TIME_LOCAL]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.SESSION_END_TIME_LOCAL',
      formatter: InsightsFormatterType.Date,
      date: true,
    },
    [Dimensions.SESSION_START_DATE_TIME_LOCAL]: {
      type: InsightsGroupByTypes.Info,
      label:
        'insightsPortalDomain.caperReportingDashboard.dimensions.SESSION_START_DATE_TIME_LOCAL',
      formatter: InsightsFormatterType.Date,
      date: true,
    },
    [Dimensions.SESSION_END_DATE_TIME_LOCAL]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.SESSION_END_DATE_TIME_LOCAL',
      formatter: InsightsFormatterType.Date,
      date: true,
    },
    [Dimensions.ORDER_ID]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.ORDER_ID',
    },
    [Dimensions.DEVICE_ID]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.DEVICE_ID',
    },
    [Dimensions.DEVICE_NAME]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.DEVICE_NAME',
    },
    [Dimensions.CART_ID]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.CART_ID',
    },
    [Dimensions.CART_NAME]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.CART_NAME',
    },

    // Audit Dimensions
    [Dimensions.CHECKOUT_AUDIT_REQUIRED]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.CHECKOUT_AUDIT_REQUIRED',
    },
    [Dimensions.HAS_CHECKOUT_AUDIT]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.HAS_CHECKOUT_AUDIT',
    },
    [Dimensions.IS_FORCE_RESOLVED]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.IS_FORCE_RESOLVED',
    },
    [Dimensions.AUDIT_ISSUE_TYPE]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.AUDIT_ISSUE_TYPE',
    },
    [Dimensions.AUDIT_ISSUE_ID]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.AUDIT_ISSUE_ID',
    },
    [Dimensions.AUDIT_ISSUE_SUB_CATEGORY]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.AUDIT_ISSUE_SUB_CATEGORY',
    },
    [Dimensions.RESOLVE_ALL_CLICKED]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.RESOLVE_ALL_CLICKED',
    },
    [Dimensions.CHECKOUT_AUDIT_SCORE]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.CHECKOUT_AUDIT_SCORE',
    },
    [Dimensions.STAFF_BARCODE]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.STAFF_BARCODE',
    },
    [Dimensions.STAFF_USER_NAME]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.STAFF_USER_NAME',
    },
    [Dimensions.STORE_AUDIT_SCORE_THRESHOLD]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.STORE_AUDIT_SCORE_THRESHOLD',
    },
    [Dimensions.NUM_FRAUD_THRESHOLDS_EXCEEDED]: {
      type: InsightsGroupByTypes.Info,
      label:
        'insightsPortalDomain.caperReportingDashboard.dimensions.NUM_FRAUD_THRESHOLDS_EXCEEDED',
    },

    // Coupon Dimensions
    [Dimensions.COUPON_SOURCE]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.COUPON_SOURCE',
    },
    [Dimensions.HAS_COUPONS]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.HAS_COUPONS',
    },

    // QR Code Dimensions
    [Dimensions.QR_CODE_COUNT]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.QR_CODE_COUNT',
    },

    // Cart Utilization Dimensions
    [Dimensions.DAILY_DEPLOYED_UTILIZATION]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.DAILY_DEPLOYED_UTILIZATION',
    },
    [Dimensions.DAILY_AVAILABLE_UTILIZATION]: {
      type: InsightsGroupByTypes.Info,
      label: 'insightsPortalDomain.caperReportingDashboard.dimensions.DAILY_AVAILABLE_UTILIZATION',
    },
  },
})
